// src/public/tests/NumericalReasoningExamples.js
import { useState } from 'react';
import {
    Container, Paper, Typography, Button, Box, TextField, Alert,
    Divider, LinearProgress
} from '@mui/material';
import { CheckCircle, Cancel, PlayArrow } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const examples = [
    { id: 'E1', series: [1, 2, 3, 4, 5, 6], answer1: 7, answer2: 8 },
    { id: 'E2', series: [2, 4, 6, 8, 10, 12], answer1: 14, answer2: 16 },
    { id: 'E3', series: [17, 16, 15, 14, 13, 12], answer1: 11, answer2: 10 }
];

const NumericalReasoningExamples = ({ onStartTest, loading }) => {
    const { t } = useTranslation();
    const [answers, setAnswers] = useState({});
    const [feedback, setFeedback] = useState({});

    const handleAnswerChange = (exampleId, field, value) => {
        // Solo permitir números y signo negativo
        if (value !== '' && !/^-?\d*$/.test(value)) {
            return;
        }

        const numValue = value === '' ? '' : value;
        setAnswers(prev => ({
            ...prev,
            [exampleId]: {
                ...prev[exampleId],
                [field]: numValue
            }
        }));

        // Verificar respuesta inmediatamente si ambos campos están completos
        const example = examples.find(e => e.id === exampleId);
        const currentAnswers = {
            ...answers[exampleId],
            [field]: numValue
        };

        if (currentAnswers.answer1 !== undefined && currentAnswers.answer1 !== '' &&
            currentAnswers.answer2 !== undefined && currentAnswers.answer2 !== '') {
            
            const ans1 = parseInt(currentAnswers.answer1, 10);
            const ans2 = parseInt(currentAnswers.answer2, 10);
            
            const isCorrect = 
                ans1 === example.answer1 && 
                ans2 === example.answer2;
            
            setFeedback(prev => ({
                ...prev,
                [exampleId]: {
                    isCorrect,
                    correctAnswer1: example.answer1,
                    correctAnswer2: example.answer2
                }
            }));
        }
    };

    return (
        <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Typography variant="h4" gutterBottom align="center">
                    {t('reasoningTests.practiceExamples')}
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    {t('reasoningTests.practiceExamplesDescription')}
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('reasoningTests.example')} 1-3 {t('reasoningTests.of')} 3
                    </Typography>
                    <LinearProgress variant="determinate" value={100} sx={{ height: 8, borderRadius: 1 }} />
                </Box>

                {examples.map((example, idx) => (
                    <Box key={example.id} sx={{ mb: idx < examples.length - 1 ? 4 : 0 }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                            {t('reasoningTests.example')} {idx + 1}
                        </Typography>

                        {/* Secuencia de números Y campos de respuesta en la MISMA LÍNEA */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                            flexWrap: 'wrap',
                            justifyContent: 'flex-start'
                        }}>
                            {example.series.map((num, numIdx) => (
                                <Box
                                    key={numIdx}
                                    sx={{
                                        fontSize: '1.5rem',
                                        fontWeight: 'bold',
                                        color: 'text.primary',
                                        minWidth: '50px',
                                        textAlign: 'center',
                                    }}
                                >
                                    {num}
                                </Box>
                            ))}

                            {/* Campos de respuesta */}
                            <TextField
                                value={answers[example.id]?.answer1 ?? ''}
                                onChange={(e) => handleAnswerChange(example.id, 'answer1', e.target.value)}
                                variant="outlined"
                                placeholder="?"
                                disabled={feedback[example.id]?.isCorrect}
                                sx={{
                                    width: '100px',
                                    '& input': {
                                        fontSize: '1.5rem',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        padding: '10px',
                                    },
                                }}
                                inputProps={{
                                    inputMode: 'numeric',
                                    pattern: '-?[0-9]*',
                                }}
                            />

                            <TextField
                                value={answers[example.id]?.answer2 ?? ''}
                                onChange={(e) => handleAnswerChange(example.id, 'answer2', e.target.value)}
                                variant="outlined"
                                placeholder="?"
                                disabled={feedback[example.id]?.isCorrect}
                                sx={{
                                    width: '100px',
                                    '& input': {
                                        fontSize: '1.5rem',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        padding: '10px',
                                    },
                                }}
                                inputProps={{
                                    inputMode: 'numeric',
                                    pattern: '-?[0-9]*',
                                }}
                            />
                        </Box>

                        {feedback[example.id] && (
                            <Alert 
                                severity={feedback[example.id].isCorrect ? 'success' : 'error'}
                                icon={feedback[example.id].isCorrect ? <CheckCircle /> : <Cancel />}
                                sx={{ mt: 2 }}
                            >
                                {feedback[example.id].isCorrect ? (
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {t('reasoningTests.correct')}
                                    </Typography>
                                ) : (
                                    <>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {t('reasoningTests.incorrect')}
                                        </Typography>
                                        <Typography variant="body2">
                                            {t('reasoningTests.correctAnswerIs')}: {feedback[example.id].correctAnswer1}, {feedback[example.id].correctAnswer2}
                                        </Typography>
                                    </>
                                )}
                            </Alert>
                        )}

                        {/* Divider entre ejemplos (excepto el último) */}
                        {idx < examples.length - 1 && (
                            <Divider sx={{ mt: 3 }} />
                        )}
                    </Box>
                ))}

                <Divider sx={{ my: 4 }} />

                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {t('reasoningTests.readyToStart')}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrow />}
                        onClick={onStartTest}
                        disabled={loading}
                        sx={{ minWidth: '200px' }}
                    >
                        {t('reasoningTests.startTest')}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default NumericalReasoningExamples;